// Performance: Request cancellation utility
class RequestManager {
  constructor() {
    this.currentRequest = null;
  }

  async fetch(url, options = {}) {
    // Cancel previous request
    if (this.currentRequest) {
      this.currentRequest.abort();
    }

    const controller = new AbortController();
    this.currentRequest = controller;

    try {
      const response = await fetch(url, {
        ...options,
        signal: controller.signal
      });
      this.currentRequest = null;
      return response;
    } catch (error) {
      if (error.name !== 'AbortError') {
        this.currentRequest = null;
        console.error('Filter request failed:', error);
      }
      return null;
    }
  }

  cancel() {
    if (this.currentRequest) {
      this.currentRequest.abort();
      this.currentRequest = null;
    }
  }
}

class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    
    // Quick exit if no form found (prevents errors)
    const facetForm = this.querySelector('form');
    if (!facetForm) {
      console.warn('⚠️ No filter form found - filters may not be enabled in theme');
      return;
    }

    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);
    this.requestManager = new RequestManager();

    // Performance: Optimized debouncing
    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 500);

    facetForm.addEventListener('input', this.debouncedOnSubmit.bind(this));

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    // Quick exit if same params
    if (searchParams === FacetFiltersForm.searchParamsPrev && !event) return;
    
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    
    // Safe DOM element checks
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    const productGridContainer = document.getElementById('ProductGridContainer');
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    
    // Safe loading state updates
    loadingSpinners.forEach((spinner) => spinner.classList.remove('hidden'));
    if (productGridContainer) {
      const collection = productGridContainer.querySelector('.collection');
      if (collection) collection.classList.add('loading');
    }
    if (countContainer) countContainer.classList.add('loading');
    if (countContainerDesktop) countContainerDesktop.classList.add('loading');

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
        : FacetFiltersForm.renderSectionFromFetch(url, event);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static renderSectionFromFetch(url, event) {
    const instance = document.querySelector('facet-filters-form');
    if (!instance) return;

    instance.requestManager.fetch(url)
      .then((response) => {
        if (!response) return; // Request was cancelled
        return response.text();
      })
      .then((responseText) => {
        if (!responseText) return;
        
        const html = responseText;
        // Limit cache size for memory management
        if (FacetFiltersForm.filterData.length > 20) {
          FacetFiltersForm.filterData = FacetFiltersForm.filterData.slice(-10);
        }
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);
        
        // Safe animation initialization
        if (typeof initializeScrollAnimationTrigger === 'function') {
          requestAnimationFrame(() => {
            initializeScrollAnimationTrigger(html);
          });
        }
      })
      .catch((error) => {
        if (error.name !== 'AbortError') {
          console.error('Filter fetch error:', error);
        }
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const cached = FacetFiltersForm.filterData.find(filterDataUrl);
    if (!cached || !cached.html) return;

    const html = cached.html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);
    
    if (typeof initializeScrollAnimationTrigger === 'function') {
      requestAnimationFrame(() => {
        initializeScrollAnimationTrigger(html);
      });
    }
  }

  static renderProductGridContainer(html) {
    const container = document.getElementById('ProductGridContainer');
    if (!container) return;

    try {
      const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
      const newContent = parsedHTML.getElementById('ProductGridContainer');
      
      if (newContent) {
        container.innerHTML = newContent.innerHTML;
        
        // Safe scroll trigger updates
        requestAnimationFrame(() => {
          container.querySelectorAll('.scroll-trigger').forEach((element) => {
            element.classList.add('scroll-trigger--cancel');
          });
        });
      }
    } catch (error) {
      console.error('Error parsing product grid HTML:', error);
    }
  }

  static renderProductCount(html) {
    try {
      const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
      const countElement = parsedHTML.getElementById('ProductCount');
      if (!countElement) return;

      const count = countElement.innerHTML;
      const container = document.getElementById('ProductCount');
      const containerDesktop = document.getElementById('ProductCountDesktop');
      
      requestAnimationFrame(() => {
        if (container) {
          container.innerHTML = count;
          container.classList.remove('loading');
        }
        if (containerDesktop) {
          containerDesktop.innerHTML = count;
          containerDesktop.classList.remove('loading');
        }
        
        const loadingSpinners = document.querySelectorAll(
          '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
        );
        loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'));
      });
    } catch (error) {
      console.error('Error parsing product count HTML:', error);
    }
  }

  static renderFilters(html, event) {
    try {
      const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
      
      const facetDetailsElementsFromFetch = parsedHTML.querySelectorAll(
        '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
      );
      const facetDetailsElementsFromDom = document.querySelectorAll(
        '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
      );

      // Safe removal of old facets
      const facetsToRemove = Array.from(facetDetailsElementsFromDom).filter((currentElement) => {
        return !Array.from(facetDetailsElementsFromFetch).some(({ id }) => currentElement.id === id);
      });
      
      requestAnimationFrame(() => {
        facetsToRemove.forEach((element) => {
          if (element && element.parentNode) {
            element.remove();
          }
        });

        const matchesId = (element) => {
          const jsFilter = event?.target?.closest('.js-filter');
          return jsFilter ? element.id === jsFilter.id : false;
        };

        const facetsToRender = Array.from(facetDetailsElementsFromFetch).filter((element) => !matchesId(element));
        const countsToRender = Array.from(facetDetailsElementsFromFetch).find(matchesId);

        facetsToRender.forEach((elementToRender, index) => {
          if (!elementToRender.id) return;
          
          const currentElement = document.getElementById(elementToRender.id);
          
          if (currentElement) {
            currentElement.innerHTML = elementToRender.innerHTML;
          } else {
            FacetFiltersForm.insertFacetElement(elementToRender, index, facetsToRender);
          }
        });

        FacetFiltersForm.renderActiveFacets(parsedHTML);
        FacetFiltersForm.renderAdditionalElements(parsedHTML);

        if (countsToRender) {
          FacetFiltersForm.handleCountsRendering(countsToRender, event);
        }
      });
    } catch (error) {
      console.error('Error rendering filters:', error);
    }
  }

  static insertFacetElement(elementToRender, index, facetsToRender) {
    if (index > 0) {
      const previousElement = facetsToRender[index - 1];
      if (previousElement && previousElement.id && elementToRender.className === previousElement.className) {
        const previousDomElement = document.getElementById(previousElement.id);
        if (previousDomElement) {
          previousDomElement.after(elementToRender);
          return;
        }
      }
    }

    if (elementToRender.parentElement && elementToRender.parentElement.id) {
      const parentFilter = document.querySelector(`#${elementToRender.parentElement.id} .js-filter`);
      if (parentFilter) {
        parentFilter.before(elementToRender);
      }
    }
  }

  static handleCountsRendering(countsToRender, event) {
    const closestJSFilter = event?.target?.closest('.js-filter');
    if (!closestJSFilter || !closestJSFilter.id) return;

    const closestJSFilterID = closestJSFilter.id;
    const targetElement = document.getElementById(closestJSFilterID);
    
    if (targetElement) {
      FacetFiltersForm.renderCounts(countsToRender, closestJSFilter);
      FacetFiltersForm.renderMobileCounts(countsToRender, targetElement);

      const newElementSelector = targetElement.classList.contains('mobile-facets__details')
        ? '.mobile-facets__close-button'
        : '.facets__summary';
      const newElementToActivate = targetElement.querySelector(newElementSelector);

      const isTextInput = event.target.getAttribute('type') === 'text';

      if (newElementToActivate && !isTextInput) {
        requestAnimationFrame(() => {
          newElementToActivate.focus();
        });
      }
    }
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      const targetElement = document.querySelector(selector);
      if (activeFacetsElement && targetElement) {
        targetElement.innerHTML = activeFacetsElement.innerHTML;
      }
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting'];

    mobileElementSelectors.forEach((selector) => {
      const sourceElement = html.querySelector(selector);
      const targetElement = document.querySelector(selector);
      if (sourceElement && targetElement) {
        targetElement.innerHTML = sourceElement.innerHTML;
      }
    });

    const mobileFiltersForm = document.getElementById('FacetFiltersFormMobile');
    if (mobileFiltersForm) {
      const menuDrawer = mobileFiltersForm.closest('menu-drawer');
      if (menuDrawer && typeof menuDrawer.bindEvents === 'function') {
        menuDrawer.bindEvents();
      }
    }
  }

  static renderCounts(source, target) {
    const targetSummary = target.querySelector('.facets__summary');
    const sourceSummary = source.querySelector('.facets__summary');

    if (sourceSummary && targetSummary) {
      targetSummary.outerHTML = sourceSummary.outerHTML;
    }

    const targetHeaderElement = target.querySelector('.facets__header');
    const sourceHeaderElement = source.querySelector('.facets__header');

    if (sourceHeaderElement && targetHeaderElement) {
      targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
    }

    const targetWrapElement = target.querySelector('.facets-wrap');
    const sourceWrapElement = source.querySelector('.facets-wrap');

    if (sourceWrapElement && targetWrapElement) {
      const isShowingMore = Boolean(target.querySelector('show-more-button .label-show-more.hidden'));
      if (isShowingMore) {
        sourceWrapElement
          .querySelectorAll('.facets__item.hidden')
          .forEach((hiddenItem) => {
            if (hiddenItem.classList.contains('hidden')) {
              hiddenItem.classList.replace('hidden', 'show-more-item');
            }
          });
      }

      targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
    }
  }

  static renderMobileCounts(source, target) {
    const targetFacetsList = target.querySelector('.mobile-facets__list');
    const sourceFacetsList = source.querySelector('.mobile-facets__list');

    if (sourceFacetsList && targetFacetsList) {
      targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    try {
      history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
    } catch (error) {
      console.error('Error updating URL hash:', error);
    }
  }

  static getSections() {
    const productGrid = document.getElementById('product-grid');
    return productGrid && productGrid.dataset.id ? [{ section: productGrid.dataset.id }] : [];
  }

  createSearchParams(form) {
    try {
      const formData = new FormData(form);
      return new URLSearchParams(formData).toString();
    } catch (error) {
      console.error('Error creating search params:', error);
      return '';
    }
  }

  onSubmitForm(searchParams, event) {
    if (searchParams) {
      FacetFiltersForm.renderPage(searchParams, event);
    }
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const sortFilterForms = document.querySelectorAll('facet-filters-form form');
    
    if (event.target.classList.contains('mobile-facets__checkbox')) {
      const form = event.target.closest('form');
      if (form) {
        const searchParams = this.createSearchParams(form);
        this.onSubmitForm(searchParams, event);
      }
    } else {
      const forms = [];
      const currentForm = event.target.closest('form');
      const isMobile = currentForm && currentForm.id === 'FacetFiltersFormMobile';

      sortFilterForms.forEach((form) => {
        if (!isMobile) {
          if (form.id === 'FacetSortForm' || form.id === 'FacetFiltersForm' || form.id === 'FacetSortDrawerForm') {
            const params = this.createSearchParams(form);
            if (params) forms.push(params);
          }
        } else if (form.id === 'FacetFiltersFormMobile') {
          const params = this.createSearchParams(form);
          if (params) forms.push(params);
        }
      });
      
      if (forms.length > 0) {
        this.onSubmitForm(forms.join('&'), event);
      }
    }
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url = event.currentTarget.href.includes('?') 
      ? event.currentTarget.href.split('?')[1]
      : '';
    if (url) {
      FacetFiltersForm.renderPage(url);
    }
  }

  // Cleanup on disconnect
  disconnectedCallback() {
    if (this.requestManager) {
      this.requestManager.cancel();
    }
  }
}

// Initialize with empty arrays
FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);

// Safe custom element definition
if (!customElements.get('facet-filters-form')) {
  customElements.define('facet-filters-form', FacetFiltersForm);
}

// Initialize listeners after DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    FacetFiltersForm.setListeners();
  });
} else {
  FacetFiltersForm.setListeners();
}

class PriceRange extends HTMLElement {
  constructor() {
    super();
    
    // Performance: Add debouncing to price range
    this.debouncedRangeChange = debounce(this.onRangeChange.bind(this), 400);
    
    this.querySelectorAll('input').forEach((element) => {
      element.addEventListener('input', this.debouncedRangeChange);
      element.addEventListener('keydown', this.onKeyDown.bind(this));
    });
    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  onKeyDown(event) {
    if (event.metaKey) return;

    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('input');
    if (inputs.length < 2) return;
    
    const minInput = inputs[0];
    const maxInput = inputs[1];
    
    if (maxInput.value) minInput.setAttribute('data-max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('data-min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('data-min', '0');
    if (maxInput.value === '') {
      const maxValue = maxInput.getAttribute('data-max') || '10000';
      minInput.setAttribute('data-max', maxValue);
    }
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('data-min') || 0);
    const max = Number(input.getAttribute('data-max') || 10000);

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}

if (!customElements.get('price-range')) {
  customElements.define('price-range', PriceRange);
}

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    if (!facetLink) return;
    
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    if (form && typeof form.onActiveFilterClick === 'function') {
      form.onActiveFilterClick(event);
    }
  }
}

if (!customElements.get('facet-remove')) {
  customElements.define('facet-remove', FacetRemove);
}